import React, { useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    InputAdornment,
    TextField,
    Typography,
} from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import EuroIcon from '@mui/icons-material/Euro';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import SaveIcon from '@mui/icons-material/Save';
import { putGasto } from '../../services/apis';
import { getCookie } from '../../utils/cookies';
import { optimizarImagenWebp } from '../../utils/optimizarImagen';

const initialForm = {
    nombre: '',
    observacion: '',
    foto: null,
    cantidad: '',
};

export const EditarGastoDialog = ({ open, onClose, gasto, cargarDatos }) => {
    const [form, setForm] = useState(initialForm);
    const [guardando, setGuardando] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (gasto) {
            setForm({
                nombre: gasto.tipo_gasto || '',
                observacion: gasto.descripcion || '',
                foto: null,
                cantidad: gasto.cantidad ?? '',
            });
            setError('');
        }
    }, [gasto]);

    const handleChange = (event) => {
        const { name, value } = event.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleFileChange = async (event) => {
        const file = event.target.files?.[0] || null;

        if (!file) {
            setForm((prev) => ({ ...prev, foto: null }));
            return;
        }

        try {
            const imagenOptimizada = await optimizarImagenWebp(file);
            setForm((prev) => ({ ...prev, foto: imagenOptimizada }));
        } catch (error) {
            console.error('Error en optimitzar la imatge:', error);
            setForm((prev) => ({ ...prev, foto: file }));
        }
    };

    const handleClose = () => {
        if (guardando) {
            return;
        }

        setError('');
        onClose();
    };

    const validar = () => {
        if (!form.nombre.trim()) {
            return 'El nom de la despesa és obligatori.';
        }

        if (form.cantidad === '' || Number(form.cantidad) < 0) {
            return 'Introdueix una quantitat vàlida.';
        }

        return '';
    };

    const handleSubmit = async () => {
        const validationError = validar();
        if (validationError) {
            setError(validationError);
            return;
        }

        if (!gasto?.id_gasto) {
            setError("No s'ha trobat la despesa.");
            return;
        }

        const formData = new FormData();
        formData.append('token', getCookie('PHPSESSID'));
        formData.append('id_gasto', gasto.id_gasto);
        formData.append('asunto', form.nombre.trim());
        formData.append('descripcion', form.observacion.trim());
        formData.append('cantidad', form.cantidad);

        if (form.foto) {
            formData.append('foto', form.foto);
        }

        setGuardando(true);
        setError('');

        try {
            const response = await putGasto(formData);
            if (response?.success) {
                await cargarDatos?.();
                onClose();
                return;
            }

            setError(response?.message || 'Ha sorgit un error.');
        } catch (err) {
            console.error('Error en actualitzar la despesa:', err);
            setError('Ha sorgit un error.');
        } finally {
            setGuardando(false);
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle className="expense-dialog-title">
                <Box className="expense-dialog-icon">
                    <ReceiptLongIcon />
                </Box>
                <Box className="expense-dialog-heading">
                    <Typography className="accounting-eyebrow">Despesa</Typography>
                    <Typography variant="h5">
                        Editar despesa
                    </Typography>
                </Box>
            </DialogTitle>

            <DialogContent dividers className="expense-dialog-content">
                {error && (
                    <Alert severity="error" className="expense-dialog-alert">
                        {error}
                    </Alert>
                )}

                <Grid container spacing={2}>
                    <Grid size={{ xs: 12 }}>
                        <TextField
                            fullWidth
                            required
                            label="Nom de la despesa"
                            name="nombre"
                            value={form.nombre}
                            onChange={handleChange}
                            inputProps={{ maxLength: 80 }}
                        />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                        <TextField
                            fullWidth
                            multiline
                            minRows={3}
                            label="Observació"
                            name="observacion"
                            value={form.observacion}
                            onChange={handleChange}
                            inputProps={{ maxLength: 500 }}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            fullWidth
                            required
                            label="Preu"
                            name="cantidad"
                            type="number"
                            value={form.cantidad}
                            onChange={handleChange}
                            inputProps={{ min: '0', step: '0.01' }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <EuroIcon className="expense-input-icon" />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Button variant="outlined" component="label" className="expense-upload-button">
                            <AttachFileIcon />
                            {form.foto ? form.foto.name : 'Canviar foto'}
                            <input type="file" hidden accept="image/*" onChange={handleFileChange} />
                        </Button>
                    </Grid>
                </Grid>
            </DialogContent>

            <DialogActions className="expense-dialog-actions">
                <Button onClick={handleClose} disabled={guardando}>
                    Cancel·lar
                </Button>
                <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSubmit} disabled={guardando}>
                    {guardando ? 'Guardant...' : 'Guardar canvis'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
